import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useReactiveVar } from '@apollo/client';
import moment from 'moment';

import {
  AppLanguage,
  languageVar,
  persistLanguage,
} from 'core/state/languageState';

import { ensureLanguageLoaded } from '../../../i18n';

interface IUseLanguage {
  language: AppLanguage;
  setLanguage: (lang: AppLanguage) => void;
}

/**
 * Active UI language. `setLanguage` switches i18next, moment and the reactive
 * var in one step and persists the choice to localStorage — no reload needed.
 */
export const useLanguage = (): IUseLanguage => {
  const { i18n } = useTranslation();
  const language = useReactiveVar(languageVar);

  const setLanguage = useCallback(
    async (lang: AppLanguage) => {
      if (lang === languageVar()) {
        return;
      }
      // The non-build dictionary is a lazy chunk (spec 0008 round 2) —
      // await it BEFORE flipping, so no frame ever renders raw keys.
      await ensureLanguageLoaded(lang);
      i18n.changeLanguage(lang);
      moment.locale(lang);
      persistLanguage(lang);
      languageVar(lang);
    },
    [i18n],
  );

  return { language, setLanguage };
};
